import React, { useEffect, useMemo, useState, MouseEvent, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { getSessionsForEvent } from '../../../../../../store/actions/admin/create-event/session';
import { EventsState } from '../../../../../../store/types';
import { ColorKeys, DBSession, LanguagesAbbr, PageModule, Session, SessionTypesFeatures, TextOverrides } from '../../../../../../types/working-model';
import TextInput from '../../../../../general-ui/text-input/text';
import Icon, { ICONS, COLORS } from '../../../../../general-ui/icon';
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	SortableContext,
	sortableKeyboardCoordinates,
	rectSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ModalComponent from '../../../../../general-ui/modal/modal';
import { updatePageModule } from '../../../../../../store/actions/admin/create-event/session';
import Checkbox from '../../../../../general-ui/checkbox/checkbox';
import './similar-sessions.scss';
import Controls from '../../session-module-controls/controls';
import { getDefaultAgendaImage } from '../../../../../../store/utils/create-event';
import { getStylingOverrides, stylingOverrideReturns } from '../../../../../live-event/utils';
import classNames from 'classnames';
import { getTextValue, niceTime, updateTranslateKey } from '../../../../../../utils/utils';
import EditableTextSlim from '../../../../../general-ui/editable-text/editable-text-slim';
import LargeButton from '../../../../../general-ui/button/large-button';
import SessionCard from '../../../homepage/editor/agenda-editor/agenda-session-card';
import { ParamsProps } from '../../../../../live-event/live-event';
import useTranslationRoute from '../../../../../live-event/hooks/use-translation-route';
import { useTranslate } from '../../../../../../i18n/useTranslationModules';
import { useIsNewNavigation } from '../../../../../../hooks/navigation.hooks';

interface Props {
	page_module: PageModule;
	template: string;
}

export default function SimilarSessions(props: Props): JSX.Element | null {
	const { page_module, template } = props;
	const {
		CreateEventReducer: { workingEvent },
		CreateSessionReducer: { workingSession, sessionsList },
		AuthReducer: { token },
	} = useSelector((state: EventsState) => state);
	const dispatch = useDispatch();
	const { language, id } = useParams<ParamsProps>();
	const [modalOpen, setModalOpen] = useState(false);
	const [sessionToRemove, setSessionToRemove] = useState<DBSession | null>(
		null
	);
	const [selectedSessions, setSelectedSessions] = useState<number[]>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [confirmingDelete, setConfirmingDelete] = useState(false);
	const namespace = useTranslationRoute();
	const { t } = useTranslate(namespace);
	const isNewNavigation= useIsNewNavigation();

	const stylingOverrides = getStylingOverrides(page_module.styling_overrides);
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8
			}
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	function handleTextChange(key: string) {
		return (text: string) => {
			if (!workingSession) return;
			dispatch(
				updatePageModule({
					...page_module,
					content: {
						...page_module.content,
						[key]: updateTranslateKey({
							translateString: page_module?.content?.[key],
							input: text,
							baseLanguage: workingSession.default_language,
							language
						}),
					},
					is_edited: true,
				})
			);
		};
	}
	function handleOverrideChange(key: string) {
		return (overrides: TextOverrides) => {
			const updatedOverrides = {
				...page_module.styling_overrides,
				content: {
					...page_module.styling_overrides?.content,
					[key]: overrides
				}
			};
			dispatch(
				updatePageModule({
					...page_module,
					styling_overrides: updatedOverrides
				})
			);
		};
	}
	function handleDragEnd(event: any) {
		if (!page_module.modules) return;
		const _modules = page_module.modules.slice();

		const startIndex = _modules.findIndex(
			(session: DBSession) =>
				Number(session.session) === Number(event.active.id)
		);
		const newIndex = _modules.findIndex(
			(session: DBSession) =>
				Number(session.session) === Number(event.over.id)
		);

		const [module] = _modules.splice(startIndex, 1);
		_modules.splice(newIndex, 0, module);

		dispatch(
			updatePageModule({
				...page_module,
				modules: _modules,
				content_modules: _modules.map(
					(session: DBSession) => session.session
				),
			})
		);
	}

	function handleSearch({
		target: { value },
	}: React.ChangeEvent<HTMLInputElement>) {
		setSearchTerm(value);
	}

	function checkDeleteSession(session: DBSession) {
		return () => {
			setSessionToRemove(session);
			setConfirmingDelete(true);
		};
	}

	function closeModal() {
		setModalOpen(false);
	}

	function finishedSettingSessions() {
		dispatch(
			updatePageModule({
				...page_module,
				modules: sessionsList.filter((session: DBSession) =>
					selectedSessions.includes(session.session)
				),
				content_modules: selectedSessions,
				is_edited: true,
			})
		);

		setModalOpen(false);
	}

	function closeDeleteModal() {
		setConfirmingDelete(false);
	}

	function removeSession() {
		if (!page_module.modules || !sessionToRemove || !page_module.content_modules) return;
		dispatch(
			updatePageModule({
				...page_module,
				modules: page_module.modules.filter(
					(session: DBSession) =>
						session.session !== sessionToRemove.session
				),
				content_modules: page_module.content_modules.filter(
					(module: number) => module !== sessionToRemove.session
				),
			})
		);
		setSelectedSessions(prevState => {
			return prevState.filter(sessionId => sessionId !== sessionToRemove?.session);
		});

		setSessionToRemove(null);
		setConfirmingDelete(false);
	}

	const toggleSession = useCallback((session: DBSession) => {
		return () => {
			let selectedModules = [...selectedSessions];

			if (!selectedModules.includes(session.session)) {
				selectedModules = [...selectedModules, session.session];
			} else {
				selectedModules = selectedModules.filter(
					(module: number) => module !== session.session
				);
			}

			setSelectedSessions(selectedModules);
		};
	}, [selectedSessions]);

	const visibleSessions = useMemo(() => {
		const term = searchTerm
			.toLowerCase()
			.trim()
			.replace(/ /g, '');

		if (!term) { return sessionsList; }

		return sessionsList.filter((session: DBSession) => {
			const name = (session.title[language] as string)
				.toLowerCase()
				.trim()
				.replace(/ /g, '');
			return name.includes(term);
		});
	}, [searchTerm, sessionsList, language]);

	useEffect(() => {
		if (!token) return;
		const eventId = workingEvent?.uuid ? workingEvent.uuid : id;
		if (eventId) dispatch(getSessionsForEvent(token, eventId));
	}, [dispatch, id, token, workingEvent?.uuid]);

	useEffect(() => {
		// load module content into selected sessions on first render
		function loadSelectedSessions() {
			if (!page_module.content_modules) return;
			const sessions = page_module.content_modules;
			setSelectedSessions(sessions);

			// // keep order of content_modules
			const modules = sessionsList
				?.filter((s: DBSession) => sessions.includes(s.session))
				?.sort((a: DBSession, b: DBSession) => sessions.indexOf(a.session) - sessions.indexOf(b.session));

			dispatch(
				updatePageModule({
					...page_module,
					modules
				})
			);
		}
		if (page_module.content_modules && sessionsList) {
			loadSelectedSessions();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sessionsList]);

	const { custom_heading } = getTextValue(page_module?.content, language);
	//needs "agenda", "agenda-sessions-list" and the cardStyling classnames to access the session card styling
	const newEventLayoutClass = isNewNavigation ? 'agenda-ltr' : 'agenda-standard';
	const cardStyling = page_module.modules?.length === 1 ? newEventLayoutClass : "agenda-vertical_card";
	return page_module.is_on && (!workingSession?.layout?.withMenu || page_module) ? (
		<div
			className={classNames(
				"editor-wrapper similar-sessions agenda",
				cardStyling,
				stylingOverrides.backgroundColor,
				template
			)}
			style={stylingOverrides.backgroundImg}
		>
			<Controls page_module={page_module} openEditModal={() => setModalOpen(true)} />
			<div className="container">
				<EditableTextSlim
					className="evt-caption-1 similar-sessions-custom_heading"
					tagType="h5"
					value={t(`${namespace}:similar_sessions.${page_module.id}.heading`, t(`${namespace}:similar_sessions.heading`, custom_heading))}
					onChange={handleTextChange("custom_heading")}
					defaultColor={ColorKeys.bodyTextColor}
					colorOverride={page_module.styling_overrides?.content?.custom_heading?.color}
					onOverrideChange={handleOverrideChange("custom_heading")}
				/>

				<div>
					{page_module.modules?.length ? (
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleDragEnd}
							modifiers={[restrictToParentElement]}
							autoScroll={false}
						>
							<SortableContext
								items={page_module.modules.map(
									(module: DBSession) =>
										String(module.session)
								)}
								strategy={rectSortingStrategy}
							>
								<div className="agenda-sessions-list">
									{page_module.modules?.map(
										(session: DBSession, index: number) => (
											<SessionComponent
												stylingOverrides={stylingOverrides}
												template={template}
												session={session}
												key={index}
												index={index}
												id={String(session.session)}
												checkDeleteSession={
													checkDeleteSession
												}
											/>
										)
									)}
								</div>
							</SortableContext>
						</DndContext>
					) : <LargeButton
						title="Add Session"
						style={{ marginTop: 16 }}
						onClick={() => setModalOpen(true)}
						nonUpload
					/>
					}
				</div>
			</div>

			<ModalComponent
				closeable={false}
				cancellable={false}
				open={modalOpen}
				onRequestClose={closeModal}
				title={'Add Similar Session'}
				footer={
					<>
						<button
							onClick={closeModal}
							style={{ marginRight: 16 }}
						>
							Cancel
						</button>
						<button
							onClick={finishedSettingSessions}
							className="primary"
						>
							Done
						</button>
					</>
				}
			>
				<div className="select-sessions">
					<div className="modal-search">
						<TextInput
							onChange={handleSearch}
							placeholder={'Search Sessions...'}
							icon={ICONS.SEARCH}
							size={'small'}
						/>
					</div>

					<div className="sessions-list-container">
						{visibleSessions.map((session: DBSession) => (
							<AddSession
								selected={selectedSessions}
								onClick={toggleSession}
								session={session}
								key={session.session}
							/>
						))}
					</div>
				</div>
			</ModalComponent>
			<ModalComponent
				open={confirmingDelete}
				title={'Remove Session?'}
				onRequestClose={closeDeleteModal}
				cancellable
				closeable={false}
			>
				<>
					<p>
						Are you sure you want to remove{' '}
						<strong>{sessionToRemove?.title[language] ?? sessionToRemove?.title.base}</strong>?
					</p>

					<div
						style={{
							display: 'flex',
							justifyContent: 'flex-end',
							marginTop: 32,
						}}
					>
						<button onClick={closeDeleteModal} style={{ marginRight: 16 }}>Cancel</button>
						<button className="primary" onClick={removeSession}>Remove</button>
					</div>
				</>
			</ModalComponent>
		</div>
	) : null;
}

interface AddProps {
	session: DBSession;
	onClick: (session: DBSession) => () => void;
	selected: any[];
}

function AddSession(props: AddProps) {
	const { session, selected, onClick } = props;

	const [languageExpanded, setLanguageExpanded] = useState(false);

	const isSelected = useMemo(() => {
		return selected.includes(session.session);
	}, [selected, session.session]);

	const sessionTime = (session.timestamp && session.end_timestamp)
		? (niceTime(session.timestamp)
			+ ' - '
			+ niceTime(session.end_timestamp))
		: null;

	return (
		<div className="session-row-container">
			<div
				className={`session-row ${isSelected ? 'selected' : ''}`}
				onClick={(e: MouseEvent) => {
					e.stopPropagation();
					onClick(session)();
				}}
			>
				<div className="checkbox-container">
					<Checkbox
						onChange={onClick(session)}
						checked={isSelected}
						value={session.session}
						color={'white'}
						activeColor={'cyan'}
					/>
				</div>
				<div className="session-data">
					<span className="timespan">
						{!SessionTypesFeatures[session.session_type].has_start_time
							? SessionTypesFeatures[session.session_type].display_name
							: sessionTime}
					</span>
					<p>{session.title.base}</p>
					<div className="badge-container">
						<div className="badge">
							<Icon
								name={ICONS.VIEWERS_EYE}
								size={12}
								color={COLORS.WHITE}
							/>{' '}
							{SessionTypesFeatures[session.session_type].display_name}
						</div>
						{session.tracks.map((track: string) => (
							<div key={track} className="badge">
								{track}
							</div>
						))}
					</div>

					{/* <span className='question-count'>{`${quiz.questions?.length ?? '0'} question${quiz.questions?.length === 1 ? '' : 's'}`}</span> */}
				</div>
				<div className="session-languages">
					{session.languages.map((language: string, index: number) => {
						if (!languageExpanded) {
							if (index < 4) {
								return (
									<div className="language" key={language}>
										{language}
									</div>
								);
							}
						} else {
							return (
								<div className="language" key={language}>
									{language}
								</div>
							);
						}
					})}
					{(session.languages.length > 4) && !languageExpanded ? (
						<button className="no-style" onClick={() => setLanguageExpanded(true)}>
							<div className="language">
								+{session.languages.length - 4}
							</div>
						</button>
					) : <></>}
				</div>
			</div>
		</div>
	);
}

interface SessionProps {
	stylingOverrides: stylingOverrideReturns
	template: string;
	index: number;
	id: string;
	session: DBSession;
	checkDeleteSession: (session: DBSession) => () => void;
}

function SessionComponent(props: SessionProps) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
	} = useSortable({ id: props.id });

	const { session, checkDeleteSession, index, template, stylingOverrides } = props;

	const { language } = useParams() as any;

	const style = {
		transform: CSS.Transform.toString(transform),
		transition: 'none',
	};

	// need to reassign DBSession as Session here. All the needed attributes are shared but type script doesnt like it
	const _session: Session = session as any;
	return (
		<div
			ref={setNodeRef}
			style={style}
			{...attributes}
			{...listeners}
			className="similar-session-wrapper"
		>
			<SessionCard
				baseLanguage={session.default_language as LanguagesAbbr}
				language={language}
				defaultImage={getDefaultAgendaImage(template, index)}
				template={template}
				key={session.session}
				session={_session}
				stylingOverrides={stylingOverrides}
				editDisabled
			/>

			<div className="edit-actions">
				<button className="round" onClick={checkDeleteSession(session)}>
					<Icon name={ICONS.TRASH} size={12} color={COLORS.WHITE} />
				</button>
			</div>
		</div>
	);
}
